/**
 * Central i18n dictionary for the authenticated app.
 *
 * Design choices:
 *  - Plain JS object — no external lib, no codegen, easy review.
 *  - `t(key, params?)` falls back to EN, then to the key itself, so a missing
 *    translation never crashes the UI.
 *  - Backend enum strings (e.g. task status "Bekliyor", priority "Kritik")
 *    are translated at the label boundary only via `STATUS_LABELS` /
 *    `PRIORITY_LABELS` — we never mutate the data sent to the API.
 */

const STORAGE_KEY = "useen_lang";
const SUPPORTED = ["en", "tr"];

export function getStoredLang() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(v) ? v : null;
  } catch {
    return null;
  }
}

export function setStoredLang(lang) {
  if (!SUPPORTED.includes(lang)) return;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
}

// Tiny interpolation: replaces {name} placeholders with params[name].
function interpolate(str, params) {
  if (!params || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (params[k] ?? `{${k}}`));
}

export function translate(lang, key, params) {
  const dict = DICT[lang] || DICT.en;
  const val = dict[key] ?? DICT.en[key] ?? key;
  return interpolate(val, params);
}

// Backend enum → user-facing label map. Keys are the raw values stored in
// Mongo (Turkish strings — we are not changing the backend). The result is
// a UI label only; we never write these back to the server.
export const STATUS_LABELS = {
  en: {
    "Bekliyor": "Pending",
    "Uygulandı": "Applied",
    "Yeniden Kontrol Edildi": "Rechecked",
    "Tamamlandı": "Completed",
  },
  tr: {
    "Bekliyor": "Bekliyor",
    "Uygulandı": "Uygulandı",
    "Yeniden Kontrol Edildi": "Yeniden Kontrol Edildi",
    "Tamamlandı": "Tamamlandı",
  },
};

export const PRIORITY_LABELS = {
  en: { "Kritik": "Critical", "Orta": "Medium", "Düşük": "Low" },
  tr: { "Kritik": "Kritik", "Orta": "Orta", "Düşük": "Düşük" },
};

export const READINESS_LABELS = {
  en: { "Güçlü": "Strong", "Orta": "Medium", "Zayıf": "Weak", "Eksik": "Missing" },
  tr: { "Güçlü": "Güçlü", "Orta": "Orta", "Zayıf": "Zayıf", "Eksik": "Eksik" },
};

// ---------------------------------------------------------------------------
// Dictionary
// ---------------------------------------------------------------------------
// Keys are namespaced by section (`nav.*`, `dash.*`, `tasks.*`, etc.). Whenever
// you add a new visible string, add it to BOTH locales — translate() will
// fall back to EN if a TR key is missing, never crashing.
const DICT = {
  en: {
    // common
    "common.loading": "Loading...",
    "common.save": "Save",
    "common.saving": "Saving...",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.add": "Add",
    "common.required": "Required",
    "common.optional": "(optional)",
    "common.copy": "Copy",
    "common.close": "Close",
    "common.send": "Send",
    "common.generate": "Generate",
    "common.regenerate": "Regenerate",
    "common.viewAll": "View all →",
    "common.score": "Score",
    "common.you": "You",
    "common.business": "Business",
    "common.sector": "Sector",
    "common.city": "City",
    "common.website": "Website",
    "common.competitors": "Competitors",
    "common.none": "(none)",
    "common.notSet": "(not set)",

    // navigation
    "nav.overview": "Overview",
    "nav.myBusiness": "My Business",
    "nav.aiScore": "AI Score",
    "nav.questionTests": "Question Readiness Test",
    "nav.competitors": "Competitors",
    "nav.tasks": "Yusi Actions",
    "nav.contentGenerator": "Content Generator",
    "nav.reports": "Reports",
    "nav.plans": "Plans",
    "nav.settings": "Settings",
    "nav.adminPanel": "Admin Panel",
    "nav.menu.open": "Open menu",
    "nav.menu.close": "Close menu",
    "nav.activeBusiness": "Active business",
    "nav.selectBusiness": "Select business",
    "nav.addBusiness": "Add new business",
    "nav.logout": "Log out",
    "nav.plan.free": "Free plan",
    "nav.plan.admin": "Admin",
    "nav.plan.named": "{name} plan",

    // login / register
    "auth.welcomeBack": "Welcome back",
    "auth.loginSubtitle": "Log in to your uSeen account.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.loginBtn": "Log in",
    "auth.loginSuccess": "Logged in successfully",
    "auth.loginErr401": "Email or password is incorrect.",
    "auth.loginErr403default": "Your account looks inactive.",
    "auth.loginErr500": "Something went wrong while signing in. Please try again shortly.",
    "auth.loginErrNetwork": "Could not reach the server. Please try again in a few seconds.",
    "auth.loginErrDefault": "Something went wrong while signing in.",
    "auth.forgotPassword": "Forgot your password?",
    "auth.noAccount": "Don't have an account?",
    "auth.createFree": "Create a free account",
    "auth.register.title": "Start free",
    "auth.register.subtitle": "Get your AI Visibility Score in 5 minutes.",
    "auth.register.fullName": "Full Name *",
    "auth.register.fullNamePh": "Your full name",
    "auth.register.emailPh": "you@yourbusiness.com",
    "auth.register.password": "Password *",
    "auth.register.passwordPh": "At least 8 characters",
    "auth.register.passwordConfirm": "Confirm password *",
    "auth.register.passwordConfirmPh": "Re-enter your password",
    "auth.register.submit": "Create account",
    "auth.register.haveAccount": "Already have an account?",
    "auth.register.goLogin": "Log in",
    "auth.register.success": "Account created",
    "auth.register.errEmptyName": "Full Name cannot be empty.",
    "auth.register.errShortPassword": "Password must be at least 8 characters.",
    "auth.register.errPasswordMismatch": "Passwords do not match.",
    "auth.register.errKvkk": "You need to acknowledge the KVKK notice.",
    "auth.register.errTerms": "You need to accept the Terms of Service.",
    "auth.register.err400": "Could not create your account. Please check your details.",
    "auth.register.err500": "Server error. Please try again shortly.",
    "auth.register.errNetwork": "Could not reach the server. Please try again in a few seconds.",
    "auth.register.errDefault": "Something went wrong during sign up.",
    "auth.register.kvkkAck": "I have read and understood the KVKK notice.",
    "auth.register.termsAck": "I have read and accept the Terms of Service.",
    "auth.register.marketing": "I want to receive uSeen product updates, campaigns, and marketing emails.",

    // language
    "lang.label": "Language",
    "lang.en": "English",
    "lang.tr": "Türkçe",
    "lang.switchAria": "Switch to {lang}",

    // dashboard
    "dash.overviewEyebrow": "Overview · {sector} · {city}",
    "dash.scoreLabel": "AI Readiness & Visibility Score",
    "dash.overallScoreLabel": "Overall Score",
    "dash.prev": "Previous:",
    "dash.scoreCaveat": "* This score is not a live ChatGPT/Gemini ranking. It evaluates your business's AI readiness signals.",
    "dash.detailedScore": "See detailed score →",
    "dash.subscores": "Subscores (7 dimensions)",
    "dash.rescan": "Rescan",
    "dash.rescan.locked": "Rescan · Locked",
    "dash.rescan.tooltip": "Upgrade your plan to rescan",
    "dash.rescanSuccess": "Rescan completed",
    "dash.rescanError": "Rescan failed",
    "dash.rescanFatal": "The scan could not be completed. Please try again shortly.",
    "insight.title": "Yusi Score Insight",
    "insight.prev": "Previous",
    "insight.current": "Current",
    "insight.delta": "Change",
    "insight.changedSubscores": "Most changed dimensions",
    "insight.cta": "View Tasks",
    "scanProgress.title": "Yusi is scanning",
    "scanProgress.tagline": "Your AI visibility is not static. Yusi monitors changing digital signals and guides you with updated actions.",
    "scanProgress.step1": "Yusi is scanning your website…",
    "scanProgress.step2": "Checking service and location signals…",
    "scanProgress.step3": "Comparing your visibility with competitors…",
    "scanProgress.step4": "Preparing AI question tests…",
    "scanProgress.step5": "Calculating your new score and tasks…",
    "scanProgress.step6": "Preparing your results…",
    "dash.createContent": "Create content",
    "dash.goToTasks": "Go to tasks",
    "dash.tile.aiScore": "AI Score",
    "dash.tile.questions": "Question Tests",
    "dash.tile.questionsSub": "prepared questions",
    "dash.tile.competitors": "Competitor Analysis",
    "dash.tile.competitorsSub": "competitors analysed",
    "dash.tile.tasks": "Tasks",
    "dash.tile.tasksSub": "completed",
    "dash.today": "Today",
    "dash.criticalToday": "3 critical tasks to handle",
    "dash.criticalIntro": "These are the best starting points to raise your visibility. Each one makes your business clearer to AI.",
    "dash.tasksRemaining": "{n} more tasks waiting — keep going to strengthen your AI Visibility Score.",
    "dash.tasksAllDone": "All caught up — re-scan to discover the next round of opportunities.",
    "dash.noCriticalLeft": "No critical tasks pending 🎉",
    "dash.criticalGapsTitle": "Top 3 critical gaps",
    "dash.noWeaknesses": "No critical gaps detected 🎉",
    "dash.strengthsTitle": "Strengths",
    "dash.noStrengths": "No strengths detected yet",
    "dash.impact": "+{n} pts impact",
    "dash.empty.title": "Start by adding your first business",
    "dash.empty.desc": "Define your business so uSeen can analyse it.",
    "dash.empty.cta": "Add business",
    "dash.noScan.title": "No analysis yet",
    "dash.noScan.desc": "Start your first analysis to see your AI Readiness & Visibility Score.",
    "dash.noScan.cta": "Start analysis",
    "upgrade.nudge.eyebrow": "Grow on a paid plan",
    "upgrade.nudge.title": "Unlock your next visibility wins",
    "upgrade.nudge.subtitle": "Free Check gives you a snapshot. A paid plan gives you the full picture and the tools to act on it — calmly, at your pace.",
    "upgrade.nudge.bullets.tasks": "See and apply your full task list",
    "upgrade.nudge.bullets.analysis": "Deeper analysis and more AI question tests",
    "upgrade.nudge.bullets.content": "Generate more content suggestions",
    "upgrade.nudge.bullets.rescan": "Monthly rescans to measure your progress",
    "upgrade.nudge.cta": "Upgrade plan",

    // tasks
    "tasks.eyebrow": "Yusi Actions",
    "tasks.title": "Yusi is preparing actions for your business",
    "tasks.subtitle": "Yusi analyzes the gaps that affect your AI visibility, turns them into actionable improvements, and shows which steps should be prioritized.",
    "tasks.legacyHelp": "For each action you can generate content, mark it as applied, and rescan to verify. If the system cannot detect the change, you can confirm manually.",
    "tasks.cardMicrocopy": "Yusi prioritized this action to strengthen your AI visibility.",
    "tasks.empty": "No tasks have been generated yet. Run an analysis first.",
    "tasks.locked.title": "+{n} more tasks locked",
    "tasks.locked.desc": "Upgrade to Starter to see and apply all tasks tailored to your business. The free plan shows the top 3 critical gaps.",
    "tasks.col.critical": "Critical",
    "tasks.col.medium": "Medium priority",
    "tasks.col.completed": "Completed",
    "tasks.emptyCol.completed": "No completed tasks yet",
    "tasks.emptyCol.default": "No tasks in this column",
    "tasks.manualBadge": "· manually confirmed",
    "tasks.contentBtn": "Content",
    "tasks.appliedBtn": "Applied",
    "tasks.applied.success": "Task marked as 'Applied'",
    "tasks.rescanBtn": "Rescan",
    "tasks.manualBtn": "Manual confirm",
    "tasks.manualConfirm": "Are you sure you want to mark this task as completed manually?",
    "tasks.manualSuccess": "Task completed (+{n} points)",
    "tasks.manualError": "Could not mark task",
    "tasks.rescanError": "Rescan failed",
    "tasks.rescanApplied": "Task verified as applied",
    "tasks.rescanNotDetected": "We couldn't clearly detect this change on your website yet",
    "tasks.runAnalysisLink": "run an analysis",
    // Yusi Task Engine 2.0 — main categories, help levels, difficulty, impact
    "tasks.mainCat.technical_ai_readiness":   "Technical AI readiness",
    "tasks.mainCat.website_content":          "Website content",
    "tasks.mainCat.google_business_profile":  "Google Business Profile",
    "tasks.mainCat.customer_reviews":         "Customer reviews & perception",
    "tasks.mainCat.ai_search_intent":         "AI search intent / fan-out",
    "tasks.mainCat.third_party_visibility":   "Third-party visibility",
    "tasks.mainCat.visual_multimodal":        "Visual & multimodal",
    "tasks.mainCat.monthly_progress":         "Monthly progress",
    "tasks.mainCat.business_basics":          "Business basics",
    "tasks.helpLevel.sectionTitle":           "How can Yusi help with this action?",
    "tasks.helpLevel.A.label":                "Yusi can prepare it",
    "tasks.helpLevel.A.desc":                 "Yusi drafts the content without any credentials — you review and paste it into the target surface.",
    "tasks.helpLevel.B.label":                "Applies with a connection",
    "tasks.helpLevel.B.desc":                 "Yusi can apply this only if you connect the relevant account (Google Business, WordPress, etc.). Coming soon.",
    "tasks.helpLevel.C.label":                "You / your team should do it",
    "tasks.helpLevel.C.desc":                 "This action needs a physical or operational decision — Yusi can guide but a person needs to execute it.",
    "tasks.difficulty.easy":                  "Easy",
    "tasks.difficulty.medium":                "Medium",
    "tasks.difficulty.hard":                  "Advanced",
    "tasks.impact.low":                       "Low impact",
    "tasks.impact.medium":                    "Medium impact",
    "tasks.impact.high":                      "High impact",
    "tasks.prepareBtnComingSoon":             "Let Yusi prepare it · coming soon",
    // Yusi Actions — Phase 2 (Yusi prepares the work)
    "tasks.prepareBtn": "Let Yusi prepare it",
    "tasks.preparingBtn": "Yusi is preparing…",
    "tasks.prepareSuccess": "Yusi prepared the content",
    "tasks.prepareError": "Yusi had trouble preparing this output. Please try again.",
    "tasks.regenerateBtn": "Regenerate",
    "tasks.drawer.title": "Prepared by Yusi",
    "tasks.drawer.versionLabel": "Version",
    "tasks.drawer.langLabel": "Language",
    "tasks.drawer.toggleShow": "Show prepared output",
    "tasks.drawer.toggleHide": "Hide prepared output",
    "tasks.drawer.tipsTitle": "Tips from Yusi",
    "tasks.drawer.copyBtn": "Copy",
    "tasks.drawer.copyToast": "Copied to clipboard",
    "tasks.drawer.editBtn": "Edit",
    "tasks.drawer.cancelEditBtn": "Cancel",
    "tasks.drawer.saveBtn": "Save",
    "tasks.drawer.saveSuccess": "Edits saved",
    "tasks.drawer.saveError": "Could not save",
    "tasks.drawer.regenerateConfirm": "Regenerate will replace the current prepared output. Continue?",
    "tasks.drawer.empty": "No prepared output yet.",
    // Phase 3 — review/approve/reject/used
    "tasks.drawer.explainer": "Yusi prepared this output for you. You can approve it, edit it, reject it, or mark it as used.",
    "tasks.drawer.approveBtn": "Approve",
    "tasks.drawer.rejectBtn": "Reject",
    "tasks.drawer.markUsedBtn": "I used this output",
    "tasks.drawer.markUsedConfirm": "Mark this output as used and complete the task?",
    "tasks.drawer.approveSuccess": "Approved",
    "tasks.drawer.rejectSuccess": "Rejected",
    "tasks.drawer.markUsedSuccess": "Marked as used",
    "tasks.drawer.approvalError": "Could not update approval",
    "tasks.approval.pending": "Waiting approval",
    "tasks.approval.approved": "Approved",
    "tasks.approval.rejected": "Rejected",
    "tasks.approval.used": "Used",
    // Phase 4 — link from Tasks page to Yusi Permissions panel
    "tasks.permLink.text": "See what Yusi can do with your permission — and what will require account connections later.",
    "tasks.permLink.cta": "Yusi Permissions",
    // Phase 4 — Yusi Permissions (Settings panel)
    "perm.title": "Yusi Permissions",
    "perm.intro": "A clear view of what Yusi can do today, what will always need your approval, and which platforms you may connect in the future.",
    "perm.current.title": "What can Yusi do now?",
    "perm.current.explainer": "Yusi does not make changes on external platforms yet. It prepares outputs and actions for you to review, approve and use.",
    "perm.current.item1": "Analyze your business visibility",
    "perm.current.item2": "Prepare ready-to-use outputs",
    "perm.current.item3": "Generate Google Business descriptions",
    "perm.current.item4": "Generate FAQ content",
    "perm.current.item5": "Generate About Us content",
    "perm.current.item6": "Generate service descriptions",
    "perm.current.item7": "Generate schema suggestions",
    "perm.current.item8": "Let you approve, reject, edit or mark outputs as used",
    "perm.approval.title": "Actions that require your approval",
    "perm.approval.explainer": "Even when integrations are available in the future, these actions should not happen without your approval.",
    "perm.approval.item1": "Updating business descriptions",
    "perm.approval.item2": "Publishing website content",
    "perm.approval.item3": "Updating Google Business information",
    "perm.approval.item4": "Responding to reviews",
    "perm.approval.item5": "Posting social media content",
    "perm.approval.item6": "Sending customer messages",
    "perm.approval.item7": "Changing working hours, phone, address or menu information",
    "perm.future.title": "Future account connections",
    "perm.future.notConnected": "Not connected",
    "perm.future.comingSoon": "Coming soon",
    "perm.future.card.gbp": "Google Business Profile",
    "perm.future.card.website": "Website / CMS",
    "perm.future.card.wordpress": "WordPress",
    "perm.future.card.webflowEtc": "Webflow / Wix / Shopify",
    "perm.future.card.instaMeta": "Instagram / Meta",
    "perm.future.card.msgChannels": "WhatsApp / SMS / Email",
    "perm.future.card.gsc": "Google Search Console",
    "perm.levels.title": "Permission levels",
    "perm.levels.explainer": "When connections become available, you will choose one level per platform. These are explanatory — none is active yet.",
    "perm.level.prepareOnly": "Prepare only",
    "perm.level.askApproval": "Ask for approval",
    "perm.level.applyApproved": "Apply approved changes",
    "perm.level.autoComplete": "Auto-complete selected actions",
    "perm.trust.title": "You stay in control",
    "perm.trust.copy": "Yusi will not make changes to any external account without your permission. When connections become available, you will be able to see exactly which permissions you granted and revoke them anytime.",

    // AI Score page
    "score.eyebrow": "AI Readiness & Visibility Score",
    "score.caveat.important": "Important:",
    "score.caveat.body": "This score is not a live ChatGPT, Gemini, or Perplexity ranking. It measures the digital readiness of your business so AI tools can understand it better.",
    "score.subscoresTitle": "Subscores and explanations",
    "score.signalsTitle": "Concrete Visibility Signals",
    "score.signalsDesc": "Technical and content signals detected from your website and business info. These signals help AI tools understand your business better.",
    "score.nextSteps": "Recommended next steps",
    "score.contentGen": "Generate content",
    "score.sub.anlasilabilirlik.label": "Clarity",
    "score.sub.anlasilabilirlik.desc": "How clearly the site explains what you do",
    "score.sub.bulunabilirlik.label": "Findability",
    "score.sub.bulunabilirlik.desc": "Strength of sector and location signals",
    "score.sub.onerilebilirlik.label": "Recommendability",
    "score.sub.onerilebilirlik.desc": "Number of reasons an AI could recommend you",
    "score.sub.icerik_gucu.label": "Content Strength",
    "score.sub.icerik_gucu.desc": "Depth and structural quality of your content",
    "score.sub.guven_sinyali.label": "Trust Signals",
    "score.sub.guven_sinyali.desc": "Contact, address, customer cues",
    "score.sub.lokasyon_netligi.label": "Location Clarity",
    "score.sub.lokasyon_netligi.desc": "Visibility of city/district/address info",
    "score.sub.hizmet_netligi.label": "Service Clarity",
    "score.sub.hizmet_netligi.desc": "Clarity of your service and product list",
    "score.sub.niyet_kapsami.label": "AI Intent Coverage",
    "score.sub.niyet_kapsami.desc": "How well your content covers AI search intents",
    "score.signal.faq": "FAQ section present",
    "score.signal.about": "About page present",
    "score.signal.services": "Services clear",
    "score.signal.location": "Location clear",
    "score.signal.contact": "Contact info present",
    "score.signal.schema": "Schema (structured data) present",
    "score.signal.robots": "Robots.txt access OK",
    "score.signal.googleDesc": "Google Business description ready",
    "score.signal.faq.ok": "FAQ signal found on the site.",
    "score.signal.faq.bad": "No FAQ section detected on the website.",
    "score.signal.about.ok": "About signal found.",
    "score.signal.about.bad": "About page not detected.",
    "score.signal.services.ok": "Service signal found.",
    "score.signal.services.bad": "No service page or clear service list detected.",
    "score.signal.location.ok": "City / address signal visible.",
    "score.signal.location.bad": "Location info appears weak on the site.",
    "score.signal.contact.ok": "Contact signal found.",
    "score.signal.contact.bad": "No phone / contact signal detected on the site.",
    "score.signal.schema.ok": "JSON-LD detected ({types}).",
    "score.signal.schema.bad": "No LocalBusiness or FAQ schema found. Structured data helps search engines and AI systems understand your business clearly.",
    "score.signal.googleDesc.ok": "Your business description is set; make sure to keep it updated.",
    "score.signal.googleDesc.bad": "Your description is short or empty. A description up to 750 chars is recommended for the Google profile.",
    "score.signal.schema.typesUnknown": "type unknown",

    // onboarding
    "onb.eyebrow": "Business setup",
    "onb.title": "Let's get to know your business",
    "onb.subtitle": "We'll use this information to generate your AI Visibility Score and recommended tasks.",
    "onb.name": "Business name *",
    "onb.sector": "Sector *",
    "onb.select": "Select",
    "onb.location": "Location *",
    "onb.cityPh": "City",
    "onb.cityPick": "Select a city",
    "onb.districtPick": "Select a district (optional)",
    "onb.districtPh": "District / neighbourhood (optional)",
    "onb.regionPh": "Region / state (optional)",
    "onb.locationHelp": "City and region info matters for local visibility in AI answers.",
    "onb.website": "Website URL",
    "onb.maps": "Google Maps link",
    "onb.audience": "Target customer type",
    "onb.audiencePh": "You can pick more than one",
    "onb.audienceOtherPh": "Describe your target audience briefly.",
    "onb.audienceHelp": "Pick the customer groups you serve most. We use this to produce more relevant AI question tests and content suggestions.",
    "onb.desc": "Short description",
    "onb.descPh": "Introduce your business in 1-2 sentences...",
    "onb.services": "Main services / products",
    "onb.targetQuestions": "AI questions you want to target (optional)",
    "onb.targetQuestionsPh": "Write the questions your customers may ask AI tools...",
    "onb.targetQuestionsHelp": "List questions your customers might ask AI tools. One per line works fine.",
    "onb.competitors": "Competitor websites (max 3)",
    "onb.competitorPh": "https://competitor.com",
    "onb.submit": "Start analysis & see my score",
    "onb.requiredErr": "Please fill in the required fields.",
    "onb.savedToast": "Business saved",
    "onb.saveError": "Could not save business",
    "onb.previewTitle": "You'll be ready for these questions",
    "onb.previewDesc": "uSeen analyses how well prepared your business is to answer this kind of user question in AI tools.",
    "onb.previewSignals": "Signals checked in the analysis",
    "onb.previewNote": "This section is not a live ChatGPT/Gemini result. uSeen analyses whether your business is ready for these AI questions.",

    // my business
    "mb.eyebrow": "My Business",
    "mb.title": "Edit business details",
    "mb.targetCustomer": "Target customer type",
    "mb.deleteBtn": "Delete business",
    "mb.deleteConfirm": "This business and all its data will be deleted. Are you sure?",
    "mb.deleteSuccess": "Business deleted",
    "mb.deleteError": "Could not delete",
    "mb.updatedToast": "Business details updated",
    "mb.updateError": "Could not update",

    // analyze
    "analyze.eyebrow": "Analysis",
    "analyze.title": "AI readiness scan for {name}",
    "analyze.subtitle": "We scan your website and competitor sites, then produce your AI Visibility Score.",
    "analyze.startBtn": "Start analysis",
    "analyze.manualBtn": "Paste the website content manually",
    "analyze.manualHint": "If the site can't be fetched automatically or is closed, paste the content here to run the analysis.",
    "analyze.manualPh": "Paste your site's main content here...",
    "analyze.submitManual": "Analyse with this text",
    "analyze.cancel": "Cancel",
    "analyze.running": "⏳ This can take 20-60 seconds. We're analysing your site and your competitors...",
    "analyze.success": "Analysis completed",
    "analyze.error": "Analysis failed",

    // competitors
    "comp.eyebrow": "Competitor Comparison",
    "comp.title": "Side-by-side with competitors",
    "comp.lockTitle": "Competitor comparison opens with Growth plan",
    "comp.lockDesc": "Upgrade to Growth to see what your competitors do well, which signals they have that you don't, and where they fall short.",
    "comp.runAnalysisFirst": "Run an analysis first.",
    "comp.yourScore": "Your Score",
    "comp.competitorN": "Competitor {n}",
    "comp.notAdded": "not added",
    "comp.signalsTable": "Signal Comparison",
    "comp.row.about": "About",
    "comp.row.services": "Service Pages",
    "comp.row.faq": "FAQ",
    "comp.row.contact": "Contact / Location",
    "comp.missingTitle": "Signals you're missing",
    "comp.noMissing": "No notable missing signals.",
    "comp.whyStronger": "Why competitors are stronger",
    "comp.noStronger": "No notable strong competitor signals found.",
    "comp.competitorDetails": "Competitor details",
    "comp.competitorN.you": "You",
    "comp.detail.strongerThanYou": "Stronger than you",
    "comp.detail.alsoMissing": "Missing for them too",
    "comp.cell.yes": "Yes",
    "comp.cell.no": "No",

    // reports
    "reports.eyebrow": "Reports",
    "reports.title": "Progress report",
    "reports.lockTitle": "Full progress report opens with Growth plan",
    "reports.lockDesc": "Score timeline, critical gaps, and detailed technical signals are part of the full report. Starter plan includes a basic report.",
    "reports.downloadPdf": "Download as PDF",
    "reports.downloadingPdf": "Downloading...",
    "reports.lockedPdf": "Download PDF",
    "reports.lockedPdfHint": "Upgrade your plan to download PDF reports.",
    "reports.lockedPdfCta": "See plans →",
    "reports.downloadSuccess": "Your PDF report has been downloaded.",
    "reports.downloadError": "Could not download PDF.",
    "reports.kpi.current": "Current Score",
    "reports.kpi.previous": "Previous Score",
    "reports.kpi.completed": "Completed Tasks",
    "reports.kpi.open": "Open Tasks",
    "reports.timelineTitle": "Score timeline",
    "reports.timelineEmpty.title": "Not enough rescan history yet",
    "reports.timelineEmpty.desc": "When at least 2 scans are recorded, your progress comparison will appear here. Apply recommended tasks and run a rescan to track your progress.",
    "reports.techPanel": "Technical & Content Signals",
    "reports.tech.robots": "Robots.txt status",
    "reports.tech.schema": "Schema status",
    "reports.tech.faqSchema": "FAQ Schema status",
    "reports.tech.localSchema": "LocalBusiness Schema recommendation",
    "reports.tech.schemaFound": "Found on page ({types}).",
    "reports.tech.schemaMissing": "No structured data detected.",
    "reports.tech.faqSchemaFound": "FAQPage schema found.",
    "reports.tech.faqSchemaMissing": "No FAQ Schema found — adding FAQ Schema to your FAQ can boost AI readability.",
    "reports.tech.localFound": "LocalBusiness-like schema found.",
    "reports.tech.localMissing": "Adding LocalBusiness schema is recommended; it structures sector + location + contact for AI systems.",
    "reports.tech.openTasks": "Open technical tasks",
    "reports.explanation": "Explanation",
    "reports.criticalGaps": "Top critical gaps",
    "reports.nextSteps": "Recommended next steps",
    "reports.section.completed": "Completed tasks",
    "reports.section.open": "Open tasks",
    "reports.section.contents": "Generated content",
    "reports.section.rescans": "Rescan history",
    "reports.section.empty.completed": "No completed tasks yet",
    "reports.section.empty.open": "No open tasks 🎉",
    "reports.section.empty.contents": "No content generated yet",
    "reports.section.empty.rescans": "No rescans yet",
    "reports.scan": "Scan",

    // question tests
    "qt.eyebrow": "AI Question Readiness Test",
    "qt.subtitle": "This screen rates how ready your business is for user intents that AI answers might surface. It is not a live ChatGPT/Gemini result.",
    "qt.empty": "No questions have been generated yet.",
    "qt.missingSignal": "Missing signal:",
    "qt.suggestedTask": "Suggested task:",
    "qt.contentBtn": "Generate content",
    "qt.gotoTasks": "Go to tasks →",
    "qt.locked.title": "+{n} more AI question tests locked",
    "qt.locked.desc": "Upgrade to Starter or Growth to see all AI question readiness test results.",
    "qt.noScanTitle": "No analysis yet",

    // content generator
    "cg.eyebrow": "Content Generator",
    "cg.title": "Ready-to-use content in your language",
    "cg.subtitle": "Pick a content type, optionally add instructions, edit the generated text, and save.",
    "cg.usage": "Monthly usage:",
    "cg.usage.remaining": "· {n} left",
    "cg.quotaLocked.title": "You've used your content generation quota for this month",
    "cg.quotaLocked.desc": "Your current plan includes {n} content generations per month. Upgrade to Starter or Growth to generate more.",
    "cg.step1": "1. Pick a content type",
    "cg.step2": "2. Extra instructions (optional)",
    "cg.step2.ph": "e.g. family-friendly tone, vegan menu, premium voice...",
    "cg.linkedTask": "Linked task:",
    "cg.remove": "Remove",
    "cg.generateBtn": "Generate content",
    "cg.linkTaskBtn": "Link to a task",
    "cg.linkTaskTitle": "Link to a task",
    "cg.linkTaskDesc": "Link this content to a task. When the task is completed, this content stays as proof.",
    "cg.noLinkableTasks": "No linkable tasks.",
    "cg.changeType": "Change type",
    "cg.sendDev": "Send to your web developer",
    "cg.linkTaskBtn2": "Link to task",
    "cg.bindSuccess": "Linked to task",
    "cg.savedToast": "Content saved",
    "cg.copiedToast": "Copied to clipboard",
    "cg.generateErr": "Generation failed",
    "cg.outputEyebrow": "Generated content",
    "cg.tips": "Tips",
    "cg.history": "Saved content",
    "cg.pickTypeFirst": "Please pick a content type",
    "cg.schemaNote": "This code needs to be added to your website. uSeen doesn't auto-publish — copy the code and send it to your web developer or paste it into your CMS custom-code area.",
    "cg.type.google_description": "Google Business Description",
    "cg.type.google_description.desc": "Optimised description for your Google profile",
    "cg.type.faq": "FAQ",
    "cg.type.faq.desc": "Frequently Asked Questions section",
    "cg.type.about": "About",
    "cg.type.about.desc": "Introduction text for your website",
    "cg.type.service_page": "Service Page",
    "cg.type.service_page.desc": "Detailed service page content",
    "cg.type.blog": "Blog Idea",
    "cg.type.blog.desc": "Sector guide blog draft",
    "cg.type.review_request": "Review Request Message",
    "cg.type.review_request.desc": "To ask customers for reviews",
    "cg.type.instagram_bio": "Instagram Bio",
    "cg.type.instagram_bio.desc": "Short bio text",
    "cg.type.meta_description": "Meta Description",
    "cg.type.meta_description.desc": "Website meta description",
    "cg.type.localbusiness_schema": "LocalBusiness Schema",
    "cg.type.localbusiness_schema.desc": "JSON-LD structured data code",
    "cg.type.faq_schema": "FAQ Schema",
    "cg.type.faq_schema.desc": "JSON-LD FAQPage schema code",

    // settings
    "settings.eyebrow": "Settings",
    "settings.title": "Account Settings",
    "settings.accountInfo": "Account Information",
    "settings.email": "Email",
    "settings.fullName": "Full Name",
    "settings.plan": "Plan",
    "settings.myBusinesses": "My Businesses",
    "settings.newBusiness": "New business",
    "settings.noBusinesses": "No businesses added yet.",
    "settings.aiScore": "AI Score",
    "settings.session": "Session",
    "settings.langPanel": "Language",
    "settings.langPanelHint": "Used for the dashboard interface only. The landing page can be browsed in either language.",
    "locked.cta": "Upgrade plan",
  },

  tr: {
    // common
    "common.loading": "Yükleniyor...",
    "common.save": "Kaydet",
    "common.saving": "Kaydediliyor...",
    "common.cancel": "Vazgeç",
    "common.delete": "Sil",
    "common.add": "Ekle",
    "common.required": "Zorunlu",
    "common.optional": "(opsiyonel)",
    "common.copy": "Kopyala",
    "common.close": "Kapat",
    "common.send": "Gönder",
    "common.generate": "Üret",
    "common.regenerate": "Yeniden Üret",
    "common.viewAll": "Tümünü gör →",
    "common.score": "Skor",
    "common.you": "Siz",
    "common.business": "İşletme",
    "common.sector": "Sektör",
    "common.city": "Şehir",
    "common.website": "Web sitesi",
    "common.competitors": "Rakipler",
    "common.none": "(yok)",
    "common.notSet": "(belirtilmemiş)",

    // navigation
    "nav.overview": "Genel Bakış",
    "nav.myBusiness": "İşletmem",
    "nav.aiScore": "AI Skoru",
    "nav.questionTests": "Soru Hazırlık Testi",
    "nav.competitors": "Rakipler",
    "nav.tasks": "Yusi Aksiyonları",
    "nav.contentGenerator": "İçerik Üretici",
    "nav.reports": "Raporlar",
    "nav.plans": "Planlar",
    "nav.settings": "Ayarlar",
    "nav.adminPanel": "Admin Panel",
    "nav.menu.open": "Menüyü aç",
    "nav.menu.close": "Menüyü kapat",
    "nav.activeBusiness": "Aktif İşletme",
    "nav.selectBusiness": "İşletme seçin",
    "nav.addBusiness": "Yeni İşletme Ekle",
    "nav.logout": "Çıkış Yap",
    "nav.plan.free": "Ücretsiz Plan",
    "nav.plan.admin": "Admin",
    "nav.plan.named": "{name} planı",

    // login / register
    "auth.welcomeBack": "Tekrar hoş geldiniz",
    "auth.loginSubtitle": "uSeen hesabınıza giriş yapın.",
    "auth.email": "E-posta",
    "auth.password": "Şifre",
    "auth.loginBtn": "Giriş Yap",
    "auth.loginSuccess": "Giriş başarılı",
    "auth.loginErr401": "E-posta veya şifre hatalı.",
    "auth.loginErr403default": "Hesabınız pasif görünüyor.",
    "auth.loginErr500": "Giriş sırasında bir hata oluştu. Lütfen birazdan tekrar deneyin.",
    "auth.loginErrNetwork": "Sunucuya bağlanılamadı. Lütfen birkaç saniye sonra tekrar deneyin.",
    "auth.loginErrDefault": "Giriş sırasında bir hata oluştu.",
    "auth.forgotPassword": "Şifremi unuttum",
    "auth.noAccount": "Hesabınız yok mu?",
    "auth.createFree": "Ücretsiz hesap oluşturun",
    "auth.register.title": "Ücretsiz başlayın",
    "auth.register.subtitle": "5 dakikada AI Görünürlük Skorunuzu öğrenin.",
    "auth.register.fullName": "Ad Soyad *",
    "auth.register.fullNamePh": "Adınız Soyadınız",
    "auth.register.emailPh": "ornek@isletmem.com",
    "auth.register.password": "Şifre *",
    "auth.register.passwordPh": "En az 8 karakter",
    "auth.register.passwordConfirm": "Şifre tekrar *",
    "auth.register.passwordConfirmPh": "Şifreyi tekrar girin",
    "auth.register.submit": "Hesap Oluştur",
    "auth.register.haveAccount": "Zaten hesabınız var mı?",
    "auth.register.goLogin": "Giriş yapın",
    "auth.register.success": "Hesap oluşturuldu",
    "auth.register.errEmptyName": "Ad Soyad alanı boş olamaz.",
    "auth.register.errShortPassword": "Şifre en az 8 karakter olmalı.",
    "auth.register.errPasswordMismatch": "Şifreler eşleşmiyor.",
    "auth.register.errKvkk": "KVKK Aydınlatma Metni'ni onaylamanız gerekiyor.",
    "auth.register.errTerms": "Kullanım Koşulları'nı onaylamanız gerekiyor.",
    "auth.register.err400": "Kayıt oluşturulamadı. Lütfen bilgilerinizi kontrol edin.",
    "auth.register.err500": "Sunucu hatası. Lütfen birazdan tekrar deneyin.",
    "auth.register.errNetwork": "Sunucuya bağlanılamadı. Lütfen birkaç saniye sonra tekrar deneyin.",
    "auth.register.errDefault": "Kayıt sırasında bir hata oluştu.",
    "auth.register.kvkkAck": "KVKK Aydınlatma Metni'ni okudum ve anladım.",
    "auth.register.termsAck": "Kullanım Koşulları'nı okudum ve onaylıyorum.",
    "auth.register.marketing": "uSeen ürün güncellemeleri, kampanya ve pazarlama e-postaları almak istiyorum.",

    // language
    "lang.label": "Dil",
    "lang.en": "English",
    "lang.tr": "Türkçe",
    "lang.switchAria": "{lang} diline geç",

    // dashboard
    "dash.overviewEyebrow": "Genel Bakış · {sector} · {city}",
    "dash.scoreLabel": "AI Hazırlık & Görünürlük Skoru",
    "dash.overallScoreLabel": "Genel Skor",
    "dash.prev": "Önceki:",
    "dash.scoreCaveat": "* Bu skor canlı ChatGPT/Gemini sıralaması değildir. İşletmenizin dijital sinyallerinin AI hazırlık değerlendirmesidir.",
    "dash.detailedScore": "Detaylı skoru gör →",
    "dash.subscores": "Alt skorlar (7 boyut)",
    "dash.rescan": "Yeniden Tara",
    "dash.rescan.locked": "Yeniden Tara · Kilitli",
    "dash.rescan.tooltip": "Yeniden tarama için paketinizi yükseltin",
    "dash.rescanSuccess": "Yeniden tarama tamamlandı",
    "dash.rescanError": "Tarama başarısız",
    "dash.rescanFatal": "Tarama tamamlanamadı. Lütfen biraz sonra tekrar deneyin.",
    "insight.title": "Yusi Skor Yorumu",
    "insight.prev": "Önceki",
    "insight.current": "Yeni",
    "insight.delta": "Değişim",
    "insight.changedSubscores": "En çok değişen alt skorlar",
    "insight.cta": "Görevleri Gör",
    "scanProgress.title": "Yusi tarama yapıyor",
    "scanProgress.tagline": "AI görünürlüğünüz canlı bir skor değildir; zaman içinde değişen dijital sinyallerin sonucudur. Yusi bu değişimleri takip eder ve sizi güncel aksiyonlarla yönlendirir.",
    "scanProgress.step1": "Yusi web sitenizi tarıyor…",
    "scanProgress.step2": "Hizmet ve lokasyon sinyalleri kontrol ediliyor…",
    "scanProgress.step3": "Rakiplerinizle görünürlük farkı karşılaştırılıyor…",
    "scanProgress.step4": "AI soru testleri hazırlanıyor…",
    "scanProgress.step5": "Yeni skor ve görevler hesaplanıyor…",
    "scanProgress.step6": "Sonuçlar hazırlanıyor…",
    "dash.createContent": "İçerik Oluştur",
    "dash.goToTasks": "Görevlere Git",
    "dash.tile.aiScore": "AI Skoru",
    "dash.tile.questions": "Soru Testleri",
    "dash.tile.questionsSub": "hazırlanmış soru",
    "dash.tile.competitors": "Rakip Analizi",
    "dash.tile.competitorsSub": "analiz edilen rakip",
    "dash.tile.tasks": "Görevler",
    "dash.tile.tasksSub": "tamamlandı",
    "dash.today": "Bugün",
    "dash.criticalToday": "Yapılacak 3 kritik görev",
    "dash.criticalIntro": "Bu görevler görünürlüğünüzü artırmak için en doğru başlangıç noktalarıdır. Her adım, işletmenizi yapay zekâlar için daha anlaşılır hale getirir.",
    "dash.tasksRemaining": "{n} görev daha sizi bekliyor — devam ettikçe AI Görünürlük Skorunuz güçlenir.",
    "dash.tasksAllDone": "Şu an tüm görevler tamam — yeni öneriler için yeniden tarama yapabilirsiniz.",
    "dash.noCriticalLeft": "Kritik bekleyen görev yok 🎉",
    "dash.criticalGapsTitle": "En Kritik 3 Eksik",
    "dash.noWeaknesses": "Kritik eksik tespit edilmedi 🎉",
    "dash.strengthsTitle": "Güçlü Yönler",
    "dash.noStrengths": "Henüz güçlü yön tespit edilmedi",
    "dash.impact": "+{n} puan etki",
    "dash.empty.title": "İlk işletmenizi ekleyerek başlayın",
    "dash.empty.desc": "uSeen analizini görmek için önce işletmenizi tanımlayın.",
    "dash.empty.cta": "İşletme Ekle",
    "dash.noScan.title": "Henüz analiz yapılmadı",
    "dash.noScan.desc": "AI Hazırlık ve Görünürlük Skorunuzu görmek için ilk analizi başlatın.",
    "dash.noScan.cta": "Analizi Başlat",
    "upgrade.nudge.eyebrow": "Ücretli planla daha hızlı ilerleyin",
    "upgrade.nudge.title": "Bir sonraki görünürlük adımlarınızı açın",
    "upgrade.nudge.subtitle": "Free Check size bir anlık görüntü verir. Ücretli plan ise tam tabloyu ve sakince ilerlemek için ihtiyacınız olan araçları sunar.",
    "upgrade.nudge.bullets.tasks": "Tüm görev listesini görün ve uygulayın",
    "upgrade.nudge.bullets.analysis": "Daha detaylı analiz ve daha fazla AI soru testi",
    "upgrade.nudge.bullets.content": "Daha fazla içerik önerisi oluşturun",
    "upgrade.nudge.bullets.rescan": "Aylık yeniden tarama ile ilerlemenizi ölçün",
    "upgrade.nudge.cta": "Planı Yükselt",

    // tasks
    "tasks.eyebrow": "Yusi Aksiyonları",
    "tasks.title": "Yusi işletmeniz için aksiyonları hazırlıyor",
    "tasks.subtitle": "Yusi, işletmenizin yapay zekâlarda daha görünür olması için eksikleri analiz eder, uygulanabilir aksiyonlara çevirir ve hangi adımların öncelikli olduğunu gösterir.",
    "tasks.legacyHelp": "Her aksiyon için içerik üretebilir, uyguladıktan sonra yeniden taratabilirsiniz. Sistem değişikliği tespit edemezse manuel onay verebilirsiniz.",
    "tasks.cardMicrocopy": "Yusi bu aksiyonu işletmenizin AI görünürlüğünü güçlendirmek için önceliklendirdi.",
    "tasks.empty": "Henüz görev üretilmedi. Önce analiz yapın.",
    "tasks.locked.title": "+{n} ek görev kilitli",
    "tasks.locked.desc": "İşletmenize özel tüm görevleri görmek ve uygulamak için Starter planına geçin. Kritik 3 eksik ücretsiz pakette görünür.",
    "tasks.col.critical": "Kritik",
    "tasks.col.medium": "Orta Öncelik",
    "tasks.col.completed": "Tamamlandı",
    "tasks.emptyCol.completed": "Henüz tamamlanmış görev yok",
    "tasks.emptyCol.default": "Bu sütunda görev yok",
    "tasks.manualBadge": "· manuel onaylı",
    "tasks.contentBtn": "İçerik",
    "tasks.appliedBtn": "Uyguladım",
    "tasks.applied.success": "Görev 'Uygulandı' olarak işaretlendi",
    "tasks.rescanBtn": "Yeniden Tara",
    "tasks.manualBtn": "Manuel Onay",
    "tasks.manualConfirm": "Bu görevi manuel olarak tamamlandı işaretlemek istediğinizden emin misiniz?",
    "tasks.manualSuccess": "Görev tamamlandı (+{n} puan)",
    "tasks.manualError": "İşaretlenemedi",
    "tasks.rescanError": "Yeniden tarama başarısız",
    "tasks.rescanApplied": "Görev uygulandı olarak doğrulandı",
    "tasks.rescanNotDetected": "Bu değişikliği henüz web sitenizde net olarak göremedik",
    "tasks.runAnalysisLink": "analiz yapın",
    // Yusi Task Engine 2.0 — ana kategoriler, yardım seviyeleri, zorluk, etki
    "tasks.mainCat.technical_ai_readiness":   "Teknik AI Hazırlığı",
    "tasks.mainCat.website_content":          "Web Sitesi İçeriği",
    "tasks.mainCat.google_business_profile":  "Google İşletme Profili",
    "tasks.mainCat.customer_reviews":         "Yorumlar ve Müşteri Algısı",
    "tasks.mainCat.ai_search_intent":         "AI Arama Niyeti / Fan-out",
    "tasks.mainCat.third_party_visibility":   "Üçüncü Taraf Görünürlük",
    "tasks.mainCat.visual_multimodal":        "Görsel ve Multimodal",
    "tasks.mainCat.monthly_progress":         "Aylık Gelişim",
    "tasks.mainCat.business_basics":          "Temel İşletme Bilgileri",
    "tasks.helpLevel.sectionTitle":           "Yusi bu görevde nasıl yardımcı olur?",
    "tasks.helpLevel.A.label":                "Yusi hazırlayabilir",
    "tasks.helpLevel.A.desc":                 "Yusi bu görevi şifre veya bağlantı istemeden hazırlar; sen inceleyip hedef alana yapıştırırsın.",
    "tasks.helpLevel.B.label":                "Bağlantı ile uygulanabilir",
    "tasks.helpLevel.B.desc":                 "Yusi bu görevi ancak ilgili hesabı (Google İşletme, WordPress vb.) bağlarsan uygulayabilir. Yakında.",
    "tasks.helpLevel.C.label":                "Kullanıcı / ekip yapmalı",
    "tasks.helpLevel.C.desc":                 "Bu görev fiziksel veya operasyonel karar gerektirir — Yusi yol gösterebilir, ama uygulamayı bir kişi yapmalı.",
    "tasks.difficulty.easy":                  "Kolay",
    "tasks.difficulty.medium":                "Orta",
    "tasks.difficulty.hard":                  "İleri",
    "tasks.impact.low":                       "Düşük etki",
    "tasks.impact.medium":                    "Orta etki",
    "tasks.impact.high":                      "Yüksek etki",
    "tasks.prepareBtnComingSoon":             "Yusi Hazırlasın · yakında",
    // Yusi Actions — Faz 2 (Yusi işi hazırlıyor)
    "tasks.prepareBtn": "Yusi hazırlasın",
    "tasks.preparingBtn": "Yusi hazırlıyor…",
    "tasks.prepareSuccess": "Yusi içeriği hazırladı",
    "tasks.prepareError": "Yusi bu içeriği hazırlarken bir sorun yaşadı. Lütfen tekrar deneyin.",
    "tasks.regenerateBtn": "Yeniden üret",
    "tasks.drawer.title": "Yusi'nin hazırladığı içerik",
    "tasks.drawer.versionLabel": "Sürüm",
    "tasks.drawer.langLabel": "Dil",
    "tasks.drawer.toggleShow": "Hazırlanan içeriği göster",
    "tasks.drawer.toggleHide": "Hazırlanan içeriği gizle",
    "tasks.drawer.tipsTitle": "Yusi'den ipuçları",
    "tasks.drawer.copyBtn": "Kopyala",
    "tasks.drawer.copyToast": "Kopyalandı",
    "tasks.drawer.editBtn": "Düzenle",
    "tasks.drawer.cancelEditBtn": "Vazgeç",
    "tasks.drawer.saveBtn": "Kaydet",
    "tasks.drawer.saveSuccess": "Değişiklikler kaydedildi",
    "tasks.drawer.saveError": "Kaydedilemedi",
    "tasks.drawer.regenerateConfirm": "Yeniden üretmek mevcut hazırlanan içeriği değiştirir. Devam edilsin mi?",
    "tasks.drawer.empty": "Henüz hazırlanan içerik yok.",
    // Faz 3 — inceleme/onay/reddet/kullanıldı
    "tasks.drawer.explainer": "Yusi bu içeriği sizin için hazırladı. Onaylayabilir, düzenleyebilir, reddedebilir veya kullandıysanız işaretleyebilirsiniz.",
    "tasks.drawer.approveBtn": "Onayla",
    "tasks.drawer.rejectBtn": "Reddet",
    "tasks.drawer.markUsedBtn": "Bu çıktıyı kullandım",
    "tasks.drawer.markUsedConfirm": "Bu çıktıyı kullanıldı olarak işaretleyip görevi tamamlayalım mı?",
    "tasks.drawer.approveSuccess": "Onaylandı",
    "tasks.drawer.rejectSuccess": "Reddedildi",
    "tasks.drawer.markUsedSuccess": "Kullanıldı olarak işaretlendi",
    "tasks.drawer.approvalError": "Onay durumu güncellenemedi",
    "tasks.approval.pending": "Onay bekliyor",
    "tasks.approval.approved": "Onaylandı",
    "tasks.approval.rejected": "Reddedildi",
    "tasks.approval.used": "Kullanıldı",
    // Faz 4 — Görevler sayfasından Yusi İzinleri paneline link
    "tasks.permLink.text": "Yusi'nin hangi işleri sizin adınıza yapabileceğini ve gelecekte hangi bağlantıların gerekeceğini görün.",
    "tasks.permLink.cta": "Yusi İzinleri",
    // Faz 4 — Yusi İzinleri (Ayarlar paneli)
    "perm.title": "Yusi İzinleri",
    "perm.intro": "Yusi'nin bugün neleri yapabildiğini, hangi işlerin her zaman sizin onayınızı gerektireceğini ve gelecekte hangi platformlara bağlanabileceğinizi açıkça gösterir.",
    "perm.current.title": "Şu anda Yusi ne yapabiliyor?",
    "perm.current.explainer": "Yusi şu anda sizin adınıza dış platformlarda işlem yapmaz. İçerikleri ve aksiyonları hazırlar, siz onaylar veya kullanırsınız.",
    "perm.current.item1": "İşletme görünürlüğünüzü analiz eder",
    "perm.current.item2": "Kullanıma hazır çıktılar hazırlar",
    "perm.current.item3": "Google İşletme açıklamaları üretir",
    "perm.current.item4": "SSS içerikleri üretir",
    "perm.current.item5": "Hakkımızda içerikleri üretir",
    "perm.current.item6": "Hizmet açıklamaları üretir",
    "perm.current.item7": "Schema önerileri üretir",
    "perm.current.item8": "Çıktıları onaylamanıza, reddetmenize, düzenlemenize veya kullanıldı olarak işaretlemenize izin verir",
    "perm.approval.title": "Onayınız olmadan yapılmayacak işler",
    "perm.approval.explainer": "Bu işler gelecekte bağlantılar aktif olduğunda bile onayınız olmadan yapılmamalıdır.",
    "perm.approval.item1": "İşletme açıklamalarını güncellemek",
    "perm.approval.item2": "Web sitesi içeriği yayınlamak",
    "perm.approval.item3": "Google İşletme bilgilerini güncellemek",
    "perm.approval.item4": "Yorumlara yanıt vermek",
    "perm.approval.item5": "Sosyal medya içeriği paylaşmak",
    "perm.approval.item6": "Müşteri mesajları göndermek",
    "perm.approval.item7": "Çalışma saatleri, telefon, adres veya menü bilgilerini değiştirmek",
    "perm.future.title": "Gelecekte bağlanabilecek hesaplar",
    "perm.future.notConnected": "Bağlı değil",
    "perm.future.comingSoon": "Yakında",
    "perm.future.card.gbp": "Google İşletme Profili",
    "perm.future.card.website": "Web Sitesi / CMS",
    "perm.future.card.wordpress": "WordPress",
    "perm.future.card.webflowEtc": "Webflow / Wix / Shopify",
    "perm.future.card.instaMeta": "Instagram / Meta",
    "perm.future.card.msgChannels": "WhatsApp / SMS / E-posta",
    "perm.future.card.gsc": "Google Search Console",
    "perm.levels.title": "İzin Seviyeleri",
    "perm.levels.explainer": "Bağlantılar aktif olduğunda her platform için bir seviye seçeceksiniz. Bu seviyeler şu an sadece açıklayıcıdır — hiçbiri aktif değil.",
    "perm.level.prepareOnly": "Sadece hazırla",
    "perm.level.askApproval": "Onayımı al",
    "perm.level.applyApproved": "Onayladıklarımı uygula",
    "perm.level.autoComplete": "Belirli işleri otomatik tamamla",
    "perm.trust.title": "Kontrol sizde kalır",
    "perm.trust.copy": "Yusi hiçbir dış hesabınızda siz izin vermeden değişiklik yapmaz. Bağlantılar aktif olduğunda hangi alanda hangi yetkiyi verdiğinizi açıkça görebilecek ve istediğiniz zaman kaldırabileceksiniz.",

    // AI Score page
    "score.eyebrow": "AI Hazırlık ve Görünürlük Skoru",
    "score.caveat.important": "Önemli:",
    "score.caveat.body": "Bu skor canlı ChatGPT, Gemini veya Perplexity sıralaması değildir. İşletmenizin yapay zekalar tarafından daha iyi anlaşılmasına yardımcı olacak dijital hazırlık seviyesini ölçer.",
    "score.subscoresTitle": "Alt Skorlar ve Açıklamalar",
    "score.signalsTitle": "Somut Görünürlük Sinyalleri",
    "score.signalsDesc": "Web sitenizden ve işletme bilgilerinizden tespit edilen teknik ve içerik sinyalleri. Bu sinyaller yapay zekaların işletmenizi daha iyi anlamasına yardımcı olur.",
    "score.nextSteps": "Sonraki Önerilen Adımlar",
    "score.contentGen": "İçerik Üret",
    "score.sub.anlasilabilirlik.label": "Anlaşılabilirlik",
    "score.sub.anlasilabilirlik.desc": "Sitenizin ne yaptığınızı net anlatması",
    "score.sub.bulunabilirlik.label": "Bulunabilirlik",
    "score.sub.bulunabilirlik.desc": "Sektör ve lokasyon sinyallerinin gücü",
    "score.sub.onerilebilirlik.label": "Önerilebilirlik",
    "score.sub.onerilebilirlik.desc": "AI'ın sizi önerebilmesi için sebep sayısı",
    "score.sub.icerik_gucu.label": "İçerik Gücü",
    "score.sub.icerik_gucu.desc": "İçerik derinliği ve yapısal kalitesi",
    "score.sub.guven_sinyali.label": "Güven Sinyali",
    "score.sub.guven_sinyali.desc": "İletişim, adres, müşteri ipuçları",
    "score.sub.lokasyon_netligi.label": "Lokasyon Netliği",
    "score.sub.lokasyon_netligi.desc": "Şehir / ilçe / adres bilgisinin görünürlüğü",
    "score.sub.hizmet_netligi.label": "Hizmet Netliği",
    "score.sub.hizmet_netligi.desc": "Hizmet ve ürün listesinin açıklığı",
    "score.sub.niyet_kapsami.label": "AI Niyet Kapsamı",
    "score.sub.niyet_kapsami.desc": "AI arama niyetlerinde site içeriğinin kapsamı",
    "score.signal.faq": "SSS bölümü var",
    "score.signal.about": "Hakkımızda var",
    "score.signal.services": "Hizmetler net",
    "score.signal.location": "Lokasyon net",
    "score.signal.contact": "İletişim bilgisi var",
    "score.signal.schema": "Schema (yapılandırılmış veri) var",
    "score.signal.robots": "Robots.txt erişimi uygun",
    "score.signal.googleDesc": "Google işletme açıklaması hazır",
    "score.signal.faq.ok": "Sitede SSS sinyali bulundu.",
    "score.signal.faq.bad": "Web sitesinde SSS bölümü tespit edilmedi.",
    "score.signal.about.ok": "Hakkımızda sinyali bulundu.",
    "score.signal.about.bad": "Hakkımızda bölümü tespit edilmedi.",
    "score.signal.services.ok": "Hizmet sinyali bulundu.",
    "score.signal.services.bad": "Hizmet sayfası veya net hizmet listesi tespit edilmedi.",
    "score.signal.location.ok": "Şehir / adres sinyali görünür.",
    "score.signal.location.bad": "Lokasyon bilgisi sitede zayıf görünüyor.",
    "score.signal.contact.ok": "İletişim sinyali bulundu.",
    "score.signal.contact.bad": "Telefon / iletişim sinyali sitede tespit edilmedi.",
    "score.signal.schema.ok": "JSON-LD tespit edildi ({types}).",
    "score.signal.schema.bad": "Web sitenizde LocalBusiness veya FAQ schema bulunamadı. Yapılandırılmış veri, arama motorlarının ve yapay zeka sistemlerinin işletmenizi daha net anlamasına yardımcı olabilir.",
    "score.signal.googleDesc.ok": "İşletme açıklamanız mevcut; düzenli güncellediğinizden emin olun.",
    "score.signal.googleDesc.bad": "Açıklamanız kısa veya boş görünüyor. Google profili için 750 karaktere kadar bir açıklama önerilir.",
    "score.signal.schema.typesUnknown": "tip belirsiz",

    // onboarding
    "onb.eyebrow": "İşletme Kurulumu",
    "onb.title": "İşletmenizi tanıyalım",
    "onb.subtitle": "Bu bilgiler AI Görünürlük Skoru ve önerilen görevlerin üretilmesi için kullanılacak.",
    "onb.name": "İşletme adı *",
    "onb.sector": "Sektör *",
    "onb.select": "Seçiniz",
    "onb.location": "Lokasyon *",
    "onb.cityPh": "Şehir",
    "onb.cityPick": "Şehir seçiniz",
    "onb.districtPick": "İlçe seçiniz (opsiyonel)",
    "onb.districtPh": "İlçe / mahalle (opsiyonel)",
    "onb.regionPh": "Bölge / eyalet (opsiyonel)",
    "onb.locationHelp": "Yapay zeka aramalarında yerel görünürlük için şehir ve bölge bilgisi önemlidir.",
    "onb.website": "Web sitesi URL",
    "onb.maps": "Google Maps linki",
    "onb.audience": "Hedef müşteri tipi",
    "onb.audiencePh": "Birden fazla seçebilirsiniz",
    "onb.audienceOtherPh": "Hedef kitlenizi kısaca açıklayın.",
    "onb.audienceHelp": "İşletmenizin en çok hizmet verdiği müşteri gruplarını seçin. Bu bilgi, AI soru testleri ve içerik önerilerini daha doğru üretmek için kullanılır.",
    "onb.desc": "Kısa açıklama",
    "onb.descPh": "İşletmenizi 1-2 cümle ile tanıtın...",
    "onb.services": "Ana hizmetler / ürünler",
    "onb.targetQuestions": "Hedeflediğiniz AI soruları (opsiyonel)",
    "onb.targetQuestionsPh": "Örn: Kadıköy'de çocukla gidilecek restoran neresi? / Vegan menüsü olan kafe önerisi?",
    "onb.targetQuestionsHelp": "Müşterilerinizin yapay zekalara sorabileceği soruları yazın. Satır satır listeleyebilirsiniz.",
    "onb.competitors": "Rakip işletme web siteleri (en fazla 3)",
    "onb.competitorPh": "https://rakip.com",
    "onb.submit": "Analizi Başlat ve Skorumu Gör",
    "onb.requiredErr": "Lütfen zorunlu alanları doldurun.",
    "onb.savedToast": "İşletme kaydedildi",
    "onb.saveError": "Kayıt başarısız",
    "onb.previewTitle": "Bu sorular için hazırlıklı olacaksınız",
    "onb.previewDesc": "uSeen, işletmenizin yapay zekaların cevaplayabileceği bu tarz kullanıcı sorularına ne kadar hazır olduğunu analiz eder.",
    "onb.previewSignals": "Analizde kontrol edilecek sinyaller",
    "onb.previewNote": "Bu bölüm canlı ChatGPT/Gemini sonucu değildir. uSeen, işletmenizin bu tarz yapay zeka sorularına hazır olup olmadığını analiz eder.",

    // my business
    "mb.eyebrow": "İşletmem",
    "mb.title": "İşletme bilgilerini düzenle",
    "mb.targetCustomer": "Hedef müşteri tipi",
    "mb.deleteBtn": "İşletmeyi Sil",
    "mb.deleteConfirm": "İşletme ve ona bağlı tüm veriler silinecek. Emin misiniz?",
    "mb.deleteSuccess": "İşletme silindi",
    "mb.deleteError": "Silinemedi",
    "mb.updatedToast": "İşletme bilgileri güncellendi",
    "mb.updateError": "Güncellenemedi",

    // analyze
    "analyze.eyebrow": "Analiz",
    "analyze.title": "{name} için AI hazırlık taraması",
    "analyze.subtitle": "Web sitenizi ve rakip sitelerinizi tararız, AI Görünürlük Skorunuzu üretiriz.",
    "analyze.startBtn": "Analizi Başlat",
    "analyze.manualBtn": "Web sitesi metnini elle yapıştır",
    "analyze.manualHint": "Site otomatik çekilemediğinde veya kapalı olduğunda, içeriği buraya yapıştırarak analiz yapabilirsiniz.",
    "analyze.manualPh": "Sitenizdeki ana metinleri buraya yapıştırın...",
    "analyze.submitManual": "Metinle Analiz Et",
    "analyze.cancel": "Vazgeç",
    "analyze.running": "⏳ Bu işlem 20-60 saniye sürebilir. Web sitenizi ve rakiplerinizi analiz ediyoruz...",
    "analyze.success": "Analiz tamamlandı",
    "analyze.error": "Analiz başarısız",

    // competitors
    "comp.eyebrow": "Rakip Karşılaştırma",
    "comp.title": "Rakiplerle yan yana",
    "comp.lockTitle": "Rakip karşılaştırması Growth planında açılır",
    "comp.lockDesc": "Rakiplerinizin hangi konularda güçlü olduğunu, sizden hangi sinyallere sahip olduklarını ve neyi eksik bıraktıklarını görmek için Growth planına geçin.",
    "comp.runAnalysisFirst": "Önce bir analiz yapın.",
    "comp.yourScore": "Sizin Skorunuz",
    "comp.competitorN": "Rakip {n}",
    "comp.notAdded": "eklenmemiş",
    "comp.signalsTable": "Sinyal Karşılaştırması",
    "comp.row.about": "Hakkımızda",
    "comp.row.services": "Hizmet Sayfaları",
    "comp.row.faq": "SSS",
    "comp.row.contact": "İletişim / Lokasyon",
    "comp.missingTitle": "Sizde eksik olan sinyaller",
    "comp.noMissing": "Belirgin eksik sinyal bulunmadı.",
    "comp.whyStronger": "Rakipler neden daha güçlü?",
    "comp.noStronger": "Karşılaştırılacak güçlü rakip sinyali bulunamadı.",
    "comp.competitorDetails": "Rakip Detayları",
    "comp.competitorN.you": "Siz",
    "comp.detail.strongerThanYou": "Sizden Güçlü Yönleri",
    "comp.detail.alsoMissing": "Rakipte de Eksik",
    "comp.cell.yes": "Var",
    "comp.cell.no": "Yok",

    // reports
    "reports.eyebrow": "Raporlar",
    "reports.title": "Gelişim raporu",
    "reports.lockTitle": "Tam gelişim raporu Growth planında açılır",
    "reports.lockDesc": "Zaman çizelgesi, kritik eksikler ve detaylı teknik sinyaller dahil tüm rapor için Growth planına geçin. Starter pakette temel rapor mevcuttur.",
    "reports.downloadPdf": "PDF Olarak İndir",
    "reports.downloadingPdf": "İndiriliyor...",
    "reports.lockedPdf": "PDF Olarak İndir",
    "reports.lockedPdfHint": "PDF rapor indirmek için paketinizi yükseltin.",
    "reports.lockedPdfCta": "Paketleri Gör →",
    "reports.downloadSuccess": "PDF raporunuz indirildi.",
    "reports.downloadError": "PDF indirilemedi.",
    "reports.kpi.current": "Mevcut Skor",
    "reports.kpi.previous": "Önceki Skor",
    "reports.kpi.completed": "Tamamlanan Görev",
    "reports.kpi.open": "Açık Görev",
    "reports.timelineTitle": "Skor zaman çizelgesi",
    "reports.timelineEmpty.title": "Henüz yeterli yeniden tarama geçmişi yok",
    "reports.timelineEmpty.desc": "En az 2 tarama yapıldığında gelişim karşılaştırması burada görünecek. Önerilen görevleri uygulayıp tekrar tarama başlatarak ilerlemenizi izleyin.",
    "reports.techPanel": "Teknik ve İçerik Sinyalleri",
    "reports.tech.robots": "Robots.txt durumu",
    "reports.tech.schema": "Schema durumu",
    "reports.tech.faqSchema": "FAQ Schema durumu",
    "reports.tech.localSchema": "LocalBusiness Schema önerisi",
    "reports.tech.schemaFound": "Sayfada bulundu ({types}).",
    "reports.tech.schemaMissing": "Yapılandırılmış veri tespit edilmedi.",
    "reports.tech.faqSchemaFound": "FAQPage schema bulundu.",
    "reports.tech.faqSchemaMissing": "FAQ Schema bulunamadı — SSS içeriğinizi FAQ Schema ile güçlendirmek AI okunabilirliğini artırabilir.",
    "reports.tech.localFound": "LocalBusiness benzeri schema mevcut.",
    "reports.tech.localMissing": "LocalBusiness schema'sı eklenmesi önerilir; sektör + lokasyon + iletişim bilgisini AI sistemleri için yapılandırır.",
    "reports.tech.openTasks": "Eksik teknik görevler",
    "reports.explanation": "Açıklama",
    "reports.criticalGaps": "En Kritik Eksikler",
    "reports.nextSteps": "Sonraki Önerilen Adımlar",
    "reports.section.completed": "Tamamlanan görevler",
    "reports.section.open": "Açık görevler",
    "reports.section.contents": "Üretilen içerikler",
    "reports.section.rescans": "Yeniden tarama geçmişi",
    "reports.section.empty.completed": "Henüz tamamlanmış görev yok",
    "reports.section.empty.open": "Açık görev yok 🎉",
    "reports.section.empty.contents": "Henüz içerik üretilmedi",
    "reports.section.empty.rescans": "Henüz yeniden tarama yapılmadı",
    "reports.scan": "Tarama",

    // question tests
    "qt.eyebrow": "AI Soru Hazırlık Testi",
    "qt.subtitle": "Bu ekran, işletmenizin yapay zeka cevaplarında sorulabilecek kullanıcı niyetlerine ne kadar hazır olduğunu değerlendirir. Canlı ChatGPT/Gemini sonucu değildir.",
    "qt.empty": "Henüz soru üretilmemiş.",
    "qt.missingSignal": "Eksik sinyal:",
    "qt.suggestedTask": "Önerilen görev:",
    "qt.contentBtn": "İçerik Oluştur",
    "qt.gotoTasks": "Görevlere bak →",
    "qt.locked.title": "+{n} ek AI soru testi kilitli",
    "qt.locked.desc": "Tüm AI soru hazırlık testi sonuçlarını görmek için Starter veya Growth planına geçin.",
    "qt.noScanTitle": "Henüz analiz yapılmadı",

    // content generator
    "cg.eyebrow": "İçerik Üretici",
    "cg.title": "Türkçe, hazır kullanılabilir içerik",
    "cg.subtitle": "İçerik türünü seçin, isteğe bağlı talimat verin, üretilen metni düzenleyin ve kaydedin.",
    "cg.usage": "Aylık kullanım:",
    "cg.usage.remaining": "· {n} hak kaldı",
    "cg.quotaLocked.title": "Bu ay için içerik üretimi hakkınız doldu",
    "cg.quotaLocked.desc": "Mevcut paketinizde ayda {n} içerik üretimi mevcut. Daha fazla hazır içerik üretmek için Starter veya Growth planına geçin.",
    "cg.step1": "1. İçerik türü seçin",
    "cg.step2": "2. Ek talimatlar (opsiyonel)",
    "cg.step2.ph": "Örn: çocuk dostu vurgusu, vegan menü, premium ton...",
    "cg.linkedTask": "Bağlı görev:",
    "cg.remove": "Kaldır",
    "cg.generateBtn": "İçerik Üret",
    "cg.linkTaskBtn": "Göreve Bağla",
    "cg.linkTaskTitle": "Bir göreve bağla",
    "cg.linkTaskDesc": "Bu içeriği bir göreve bağlayın. Görev tamamlandığında bu içerik kanıt olarak kalır.",
    "cg.noLinkableTasks": "Bağlanabilir görev yok.",
    "cg.changeType": "Türü Değiştir",
    "cg.sendDev": "Web geliştiricinize gönderin",
    "cg.linkTaskBtn2": "Göreve Bağla",
    "cg.bindSuccess": "Göreve bağlandı",
    "cg.savedToast": "İçerik kaydedildi",
    "cg.copiedToast": "Panoya kopyalandı",
    "cg.generateErr": "Üretim başarısız",
    "cg.outputEyebrow": "Üretilen İçerik",
    "cg.tips": "İpuçları",
    "cg.history": "Kaydedilen içerikler",
    "cg.pickTypeFirst": "Lütfen bir içerik türü seçin",
    "cg.schemaNote": "Bu kodun web sitenize eklenmesi gerekir. uSeen otomatik yayınlama yapmaz — kodu kopyalayıp web geliştiricinize iletebilir veya CMS'inizdeki özel kod alanına yapıştırabilirsiniz.",
    "cg.type.google_description": "Google İşletme Açıklaması",
    "cg.type.google_description.desc": "Google profili için optimize edilmiş açıklama",
    "cg.type.faq": "SSS",
    "cg.type.faq.desc": "Sık Sorulan Sorular bölümü",
    "cg.type.about": "Hakkımızda",
    "cg.type.about.desc": "Web sitesi için tanıtım metni",
    "cg.type.service_page": "Hizmet Sayfası",
    "cg.type.service_page.desc": "Detaylı hizmet sayfası içeriği",
    "cg.type.blog": "Blog Fikri",
    "cg.type.blog.desc": "Sektörel rehber blog taslağı",
    "cg.type.review_request": "Yorum İsteme Mesajı",
    "cg.type.review_request.desc": "Müşterilerden yorum istemek için",
    "cg.type.instagram_bio": "Instagram Bio",
    "cg.type.instagram_bio.desc": "Kısa biyografi metni",
    "cg.type.meta_description": "Meta Açıklama",
    "cg.type.meta_description.desc": "Web sitesi meta description",
    "cg.type.localbusiness_schema": "LocalBusiness Schema",
    "cg.type.localbusiness_schema.desc": "JSON-LD yapılandırılmış veri kodu",
    "cg.type.faq_schema": "FAQ Schema",
    "cg.type.faq_schema.desc": "JSON-LD FAQPage schema kodu",

    // settings
    "settings.eyebrow": "Ayarlar",
    "settings.title": "Hesap Ayarları",
    "settings.accountInfo": "Hesap Bilgileri",
    "settings.email": "E-posta",
    "settings.fullName": "Ad Soyad",
    "settings.plan": "Plan",
    "settings.myBusinesses": "İşletmelerim",
    "settings.newBusiness": "Yeni İşletme",
    "settings.noBusinesses": "Henüz işletme eklenmemiş.",
    "settings.aiScore": "AI Skoru",
    "settings.session": "Oturum",
    "settings.langPanel": "Dil",
    "settings.langPanelHint": "Sadece panel arayüzü için kullanılır. Tanıtım sayfası her iki dilde de gezilebilir.",
    "locked.cta": "Paketimi Yükselt",
  },
};

export const SUPPORTED_LANGS = SUPPORTED;
